import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "../api/axios";
import { 
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaComment,
  FaPaperPlane,
  FaCheckCircle,
  FaHeadset,
  FaBuilding,
  FaMobile,
  FaQrcode,
  FaTelegram,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaLinkedin,
  FaGlobe,
  FaHandshake,
  FaArrowRight,
  FaMapMarkedAlt,
  FaStore,
  FaTruck,
  FaUserTie
} from "react-icons/fa";

// Color palette
const colors = {
  brightBlue: '#00A9EC',
  deepOrange: '#E06207',
  nearBlack: '#0E1306',
  green: '#018F2C',
  darkBrown: '#602005',
  burntOrange: '#A65007',
  lightOrange: '#EF9222',
  darkGreen: '#056619',
  reddishBrown: '#9C2E04',
  darkBlue: '#0067A1',
  skyBlue: '#0DC8F8',
  peachOrange: '#FDA653',
  navyBlue: '#023057',
  oliveBrown: '#604B0C',
  tealBlue: '#024D6E',
  pinkMagenta: '#A70756',
  goldenYellow: '#F3D71B',
  freshGreen: '#12B14A',
  lightGreen: '#55CB71',
  lightCyan: '#56E0F9'
};

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Submitting...'
      });

      const response = await axios.post('/contact', formData);
      
      setFormStatus({
        submitted: true,
        success: true,
        message: response.data.message || 'Thank you for contacting us! We will get back to you soon.'
      });
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setFormStatus({ submitted: false, success: false, message: '' });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      }, 5000);
    } catch (error) {
      setFormStatus({
        submitted: true,
        success: false,
        message: error.response?.data?.message || 'Failed to submit form. Please try again.'
      });
      
      // Reset error message after 5 seconds
      setTimeout(() => {
        setFormStatus({ submitted: false, success: false, message: '' });
      }, 5000);
    }
  };

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="bg-white overflow-x-hidden mt-8">
      {/* ===== HERO SECTION ===== */}
      <section 
        className="pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20 text-white relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${colors.navyBlue} 0%, ${colors.darkBlue} 100%)`
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-48 sm:w-64 h-48 sm:h-64 rounded-full bg-white"></div>
          <div className="absolute bottom-20 right-10 w-64 sm:w-96 h-64 sm:h-96 rounded-full bg-white"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* ABM Logo Animation */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex items-center justify-center gap-2 sm:gap-4 mb-6 sm:mb-8"
            >
              <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center text-white font-bold text-2xl sm:text-4xl shadow-2xl"
                style={{ backgroundColor: colors.pinkMagenta, color: "blue" }}
              >
                A
              </div>
              <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center font-bold text-2xl sm:text-4xl shadow-2xl"
                style={{ backgroundColor: colors.freshGreen, color: "blue" }}
              >
                B
              </div>
              <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center text-white font-bold text-2xl sm:text-4xl shadow-2xl"
                style={{ backgroundColor: colors.goldenYellow, color: "blue" }}
              >
                M
              </div>
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 px-4">
              Contact Us
            </h1>
            <p className="text-lg sm:text-xl mb-3 sm:mb-4" style={{ color: colors.lightOrange }}>
              We're Here to Help You
            </p>
            <p className="text-base sm:text-lg text-white/80 max-w-3xl mx-auto px-4">
              Have questions about our plans, need support, or want to partner with us? 
              Our team is ready to assist you 24/7.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== QUICK CONTACT CARDS ===== */}
      <section className="py-8 sm:py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-6xl mx-auto -mt-12 sm:-mt-16 relative z-20">
            {[
              { 
                icon: FaPhone, 
                title: 'Call Us', 
                value: '8887355992',
                sub: 'Toll Free',
                color: colors.brightBlue,
                action: 'tel:8887355992'
              },
              { 
                icon: FaWhatsapp, 
                title: 'WhatsApp', 
                value: '+91 8887355992',
                sub: '24/7 Support',
                color: colors.freshGreen,
                action: 'https://wa.me/918887355992'
              },
              { 
                icon: FaEnvelope, 
                title: 'Email Us', 
                value: 'support.abmgroupone@gmail.com',
                sub: 'Reply within 24h',
                color: colors.deepOrange,
                action: 'mailto:support.abmgroupone@gmail.com'
              },
              { 
                icon: FaHeadset, 
                title: 'Live Chat', 
                value: 'Chat Now',
                sub: 'Online 10AM-6PM',
                color: colors.pinkMagenta,
                action: '#chat'
              }
            ].map((item, idx) => (
              <motion.a
                key={idx}
                href={item.action}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-lg sm:rounded-xl shadow-lg sm:shadow-xl p-4 sm:p-6 text-center hover:shadow-2xl transition transform hover:-translate-y-2 cursor-pointer"
                target={item.action.startsWith('http') ? '_blank' : '_self'}
                rel="noopener noreferrer"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full mx-auto mb-2 sm:mb-4 flex items-center justify-center text-xl sm:text-3xl"
                  style={{ backgroundColor: item.color + '20', color: item.color }}>
                  <item.icon />
                </div>
                <h3 className="font-bold text-sm sm:text-base lg:text-lg mb-1">{item.title}</h3>
                <p className="text-xs sm:text-sm font-semibold truncate" style={{ color: item.color }}>{item.value}</p>
                <p className="text-xs mt-1 sm:mt-2" style={{ color: colors.oliveBrown }}>{item.sub}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT FORM & INFO ===== */}
      <section className="py-12 sm:py-16 md:py-20" style={{ backgroundColor: '#f8fafc' }}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-6 sm:p-8 order-2 md:order-1"
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6" style={{ color: colors.nearBlack }}>
                Send Us a Message
              </h2>
              
              {formStatus.submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-6 text-center"
                >
                  <FaCheckCircle className="text-4xl sm:text-5xl mx-auto mb-3 sm:mb-4" style={{ color: colors.freshGreen }} />
                  <h3 className="text-lg sm:text-xl font-bold mb-2" style={{ color: colors.darkGreen }}>
                    Thank You!
                  </h3>
                  <p className="text-sm sm:text-base" style={{ color: colors.oliveBrown }}>{formStatus.message}</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2" style={{ color: colors.oliveBrown }}>
                      Your Name *
                    </label>
                    <div className="relative">
                      <FaUser className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm sm:text-base" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full pl-8 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 transition"
                        style={{ focusRingColor: colors.brightBlue }}
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2" style={{ color: colors.oliveBrown }}>
                        Email Address *
                      </label>
                      <div className="relative">
                        <FaEnvelope className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm sm:text-base" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full pl-8 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                          style={{ focusRingColor: colors.brightBlue }}
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2" style={{ color: colors.oliveBrown }}>
                        Phone Number *
                      </label>
                      <div className="relative">
                        <FaPhone className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm sm:text-base" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full pl-8 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                          style={{ focusRingColor: colors.brightBlue }}
                          placeholder="Enter your phone"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2" style={{ color: colors.oliveBrown }}>
                      Subject *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                      style={{ focusRingColor: colors.brightBlue }}
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="partnership">Partnership Opportunity</option>
                      <option value="pickup">My Pickup Center</option>
                      <option value="store">Mobile Store</option>
                      <option value="complaint">Complaint</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2" style={{ color: colors.oliveBrown }}>
                      Your Message *
                    </label>
                    <div className="relative">
                      <FaComment className="absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-400 text-sm sm:text-base" />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="4"
                        className="w-full pl-8 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                        style={{ focusRingColor: colors.brightBlue }}
                        placeholder="Type your message here..."
                      ></textarea>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 sm:py-4 rounded-lg font-bold text-sm sm:text-base text-white transition transform hover:-translate-y-1 hover:shadow-xl flex items-center justify-center gap-2"
                    style={{ 
                      background: `linear-gradient(135deg, ${colors.deepOrange}, ${colors.burntOrange})`
                    }}
                  >
                    <FaPaperPlane className="text-sm sm:text-base" /> Send Message
                  </button>

                  <p className="text-xs text-center" style={{ color: colors.oliveBrown }}>
                    * Required fields. We'll respond within 24 hours.
                  </p>
                </form>
              )}
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 md:order-2"
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6" style={{ color: colors.nearBlack }}>
                Get in Touch
              </h2>
              
              <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                {/* Head Office */}
                <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-md sm:shadow-lg">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-lg sm:text-2xl flex-shrink-0"
                      style={{ backgroundColor: colors.brightBlue + '20', color: colors.brightBlue }}>
                      <FaBuilding />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: colors.brightBlue }}>Uttar Pradesh</p>
                      <h3 className="font-bold text-base sm:text-lg mb-1 sm:mb-2">Head Office</h3>
                      <p className="text-xs sm:text-sm" style={{ color: colors.oliveBrown }}>
                        Ramnagar,<br />
                        Prayagraj - 212305,<br />
                        Uttar Pradesh, India
                      </p>
                    </div>
                  </div>
                </div>

                {/* Registered Office */}
                <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-md sm:shadow-lg">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-lg sm:text-2xl flex-shrink-0"
                      style={{ backgroundColor: colors.freshGreen + '20', color: colors.freshGreen }}>
                      <FaMapMarkedAlt />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: colors.freshGreen }}>Uttar Pradesh</p>
                      <h3 className="font-bold text-base sm:text-lg mb-1 sm:mb-2">Registered Office</h3>
                      <p className="text-xs sm:text-sm" style={{ color: colors.oliveBrown }}>
                        Ramnagar,<br />
                        Prayagraj - 212305,<br />
                        Uttar Pradesh, India
                      </p>
                    </div>
                  </div>
                </div>

                {/* Working Office */}
                <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-md sm:shadow-lg">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-lg sm:text-2xl flex-shrink-0"
                      style={{ backgroundColor: colors.deepOrange + '20', color: colors.deepOrange }}>
                      <FaBuilding />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: colors.deepOrange }}>UP</p>
                      <h3 className="font-bold text-base sm:text-lg mb-1 sm:mb-2">Working Office</h3>
                      <p className="text-xs sm:text-sm" style={{ color: colors.oliveBrown }}>
                        Naini,<br />
                        Prayagraj - 211008,<br />
                        Uttar Pradesh, India
                      </p>
                    </div>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-md sm:shadow-lg">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-lg sm:text-2xl flex-shrink-0"
                      style={{ backgroundColor: colors.deepOrange + '20', color: colors.deepOrange }}>
                      <FaClock />
                    </div>
                    <div className="w-full">
                      <h3 className="font-bold text-base sm:text-lg mb-1 sm:mb-2">Working Hours</h3>
                      <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                        <div className="flex justify-between">
                          <span>Monday - Friday</span>
                          <span className="font-bold">10AM - 6PM</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Saturday</span>
                          <span className="font-bold">10AM - 4PM</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Sunday</span>
                          <span className="font-bold" style={{ color: colors.reddishBrown }}>Closed</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Emergency Support */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-red-100 flex items-center justify-center text-lg sm:text-2xl flex-shrink-0"
                      style={{ color: colors.reddishBrown }}>
                      <FaHeadset />
                    </div>
                    <div>
                      <h3 className="font-bold text-base sm:text-lg">24/7 Emergency Support</h3>
                      <p className="text-xs sm:text-sm mb-1 sm:mb-2">For urgent issues outside business hours</p>
                      <a href="tel:8887355992" className="text-base sm:text-xl font-bold" style={{ color: colors.deepOrange }}>
                        +91 8887355992
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-md sm:shadow-lg">
                <h3 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">Connect With Us</h3>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {[
                    { icon: FaFacebook, color: '#1877F2', link: 'https://facebook.com/abmgroup' },
                    { icon: FaTwitter, color: '#1DA1F2', link: 'https://twitter.com/abmgroup' },
                    { icon: FaInstagram, color: '#E4405F', link: 'https://instagram.com/abmgroup' },
                    { icon: FaYoutube, color: '#FF0000', link: 'https://youtube.com/abmgroup' },
                    { icon: FaLinkedin, color: '#0A66C2', link: 'https://linkedin.com/company/abmgroup' },
                    { icon: FaTelegram, color: '#26A5E4', link: 'https://t.me/abmgroup' }
                  ].map((social, idx) => (
                    <motion.a
                      key={idx}
                      href={social.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ y: -5 }}
                      className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center text-base sm:text-lg lg:text-2xl text-white"
                      style={{ backgroundColor: social.color }}
                    >
                      <social.icon />
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== MAP SECTION ===== */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-6 sm:mb-8"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-4" style={{ color: colors.nearBlack }}>
              Find Us on Map
            </h2>
            <p className="text-sm sm:text-base lg:text-lg" style={{ color: colors.oliveBrown }}>
              Visit our headquarters or regional offices
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-slate-200 h-64 sm:h-80 lg:h-96 rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl relative"
          >
            {/* Map Placeholder - Replace with actual Google Maps embed */}
            <div className="absolute inset-0 flex items-center justify-center bg-slate-300">
              <div className="text-center px-4">
                <FaMapMarkerAlt className="text-4xl sm:text-5xl lg:text-6xl mx-auto mb-2 sm:mb-4" style={{ color: colors.deepOrange }} />
                <p className="text-base sm:text-lg font-bold" style={{ color: colors.nearBlack }}>ABM Group One Headquarters</p>
                <p className="text-xs sm:text-sm" style={{ color: colors.oliveBrown }}>Prayagraj, Uttar Pradesh - 211008</p>
                <p className="text-xs mt-2 sm:mt-4 text-gray-500">Google Maps Integration will be added here</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== DEPARTMENT CONTACTS ===== */}
      <section className="py-12 sm:py-16 lg:py-20" style={{ backgroundColor: '#f8fafc' }}>
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-4" style={{ color: colors.nearBlack }}>
              Department Contacts
            </h2>
            <p className="text-base sm:text-lg lg:text-xl" style={{ color: colors.oliveBrown }}>
              Reach the right team directly
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 max-w-5xl mx-auto">
            {[
              {
                dept: 'Member Support',
                email: 'support.abmgroupone@gmail.com',
                phone: '+91 8887355992',
                hours: '10AM - 6PM',
                icon: FaUser,
                color: colors.brightBlue
              },
              {
                dept: 'Pickup Center',
                email: 'support.abmgroupone@gmail.com',
                phone: '+91 8887355992',
                hours: '10AM - 6PM',
                icon: FaStore,
                color: colors.deepOrange
              },
              {
                dept: 'Mobile Store',
                email: 'support.abmgroupone@gmail.com',
                phone: '+91 8887355992',
                hours: '10AM - 6PM',
                icon: FaTruck,
                color: colors.freshGreen
              },
              {
                dept: 'Partnership',
                email: 'support.abmgroupone@gmail.com',
                phone: '+91 8887355992',
                hours: '11AM - 5PM',
                icon: FaHandshake,
                color: colors.goldenYellow
              },
              {
                dept: 'Technical Support',
                email: 'support.abmgroupone@gmail.com',
                phone: '+91 8887355992',
                hours: '24/7',
                icon: FaHeadset,
                color: colors.pinkMagenta
              },
              {
                dept: 'Accounts & Finance',
                email: 'support.abmgroupone@gmail.com',
                phone: '+91 8887355992',
                hours: '10AM - 5PM',
                icon: FaBuilding,
                color: colors.tealBlue
              }
            ].map((dept, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-md hover:shadow-xl transition"
              >
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center text-base sm:text-lg lg:text-2xl"
                    style={{ backgroundColor: dept.color + '20', color: dept.color }}>
                    <dept.icon />
                  </div>
                  <h3 className="font-bold text-sm sm:text-base">{dept.dept}</h3>
                </div>
                <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                  <p className="break-all">
                    <span className="font-semibold">Email:</span>{' '}
                    <a href={`mailto:${dept.email}`} className="text-xs sm:text-sm" style={{ color: dept.color }}>
                      {dept.email}
                    </a>
                  </p>
                  <p>
                    <span className="font-semibold">Phone:</span>{' '}
                    <a href={`tel:${dept.phone}`} className="text-xs sm:text-sm" style={{ color: dept.color }}>
                      {dept.phone}
                    </a>
                  </p>
                  <p className="text-xs" style={{ color: colors.oliveBrown }}>
                    Hours: {dept.hours}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ PREVIEW ===== */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
            
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-4" style={{ color: colors.nearBlack }}>
                Frequently Asked Questions
              </h2>
              <p className="text-base sm:text-lg mb-4 sm:mb-6" style={{ color: colors.oliveBrown }}>
                Quick answers to common questions
              </p>
              
              <div className="space-y-3 sm:space-y-4">
                {[
                  {
                    q: 'How do I register?',
                    a: 'Click on Register button, fill your details with sponsor ID, and complete KYC.'
                  },
                  {
                    q: 'What is the minimum withdrawal?',
                    a: 'Minimum withdrawal is ₹200 in round figures (₹200, ₹300, ₹500 etc.)'
                  },
                  {
                    q: 'How long does payout take?',
                    a: 'PU&AP Wallet payments are processed on V+2 cycle. E-Wallet withdrawals are instant after approval.'
                  }
                ].map((faq, idx) => (
                  <div key={idx} className="bg-slate-50 p-3 sm:p-4 rounded-lg">
                    <p className="font-bold text-sm sm:text-base mb-1 sm:mb-2">{faq.q}</p>
                    <p className="text-xs sm:text-sm" style={{ color: colors.oliveBrown }}>{faq.a}</p>
                  </div>
                ))}
              </div>

              <Link
                to="/faq"
                className="inline-flex items-center gap-2 mt-4 sm:mt-6 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base"
                style={{ 
                  backgroundColor: colors.deepOrange,
                  color: 'white'
                }}
              >
                View All FAQs <FaArrowRight className="text-xs sm:text-sm" />
              </Link>
            </motion.div>

            {/* Right Content - QR Code */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mt-6 lg:mt-0"
            >
              <div className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-lg sm:shadow-xl inline-block">
                <div className="w-36 h-36 sm:w-40 sm:h-40 lg:w-48 lg:h-48 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                  <FaQrcode className="text-6xl sm:text-7xl lg:text-8xl text-white opacity-50" />
                </div>
                <h3 className="font-bold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2">Scan to Connect</h3>
                <p className="text-xs sm:text-sm" style={{ color: colors.oliveBrown }}>
                  Scan QR code to save contact <br /> directly to your phone
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-12 sm:py-16 lg:py-20 relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${colors.navyBlue}, ${colors.darkBlue})`
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-48 sm:w-64 h-48 sm:h-64 rounded-full bg-white"></div>
          <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 rounded-full bg-white"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center text-white">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 px-4">
            Need Immediate Assistance?
          </h2>
          <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 max-w-2xl mx-auto px-4" style={{ color: colors.lightOrange }}>
            Our support team is available 24/7 to help you
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <a
              href="tel:8887355992"
              className="px-6 sm:px-8 lg:px-10 py-3 sm:py-4 bg-white rounded-lg font-bold text-sm sm:text-base lg:text-lg hover:shadow-2xl transition transform hover:-translate-y-1 flex items-center justify-center gap-2"
              style={{ color: colors.navyBlue }}
            >
              <FaPhone className="text-sm sm:text-base" /> Call Now
            </a>
            <a
              href="https://wa.me/918887355992"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 sm:px-8 lg:px-10 py-3 sm:py-4 border-2 border-white rounded-lg font-bold text-sm sm:text-base lg:text-lg hover:bg-white/20 transition flex items-center justify-center gap-2"
            >
              <FaWhatsapp className="text-sm sm:text-base" /> WhatsApp
            </a>
          </div>

          <div className="mt-6 sm:mt-8 text-xs sm:text-sm text-white/80 px-4">
            <p className="break-all">📧 support.abmgroupone@gmail.com | 📞 +91 8887355992</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;